<template>
  <div class="page-container animate-fade-in-up">
    <PageHeader icon="el-icon-user" title="请选择评估对象" subtitle="选择一位员工进行评估，系统将基于其行为特征计算离职风险分数" />

    <div class="toolbar">
      <div class="search-bar">
        <i class="el-icon-search search-icon"></i>
        <input
          v-model="searchKey"
          class="search-input"
          placeholder="输入姓名搜索..."
          aria-label="搜索员工姓名"
          @input="handleSearch"
        />
        <i v-if="searchKey" class="el-icon-circle-close search-clear" @click="searchKey = ''"></i>
      </div>
      <div class="stat-summary">
        <span class="stat-item">共 {{ employees.length }} 人</span>
        <span class="stat-item stat-high">{{ highRiskCount }} 高风险</span>
        <span class="stat-item stat-medium">{{ mediumRiskCount }} 中风险</span>
        <span class="stat-item stat-low">{{ lowRiskCount }} 低风险</span>
        <span class="stat-item stat-unrated">{{ unratedCount }} 未评估</span>
        <el-button v-if="!$store.getters.isCurrentTaskExpired && isAdmin" size="mini" type="primary" icon="el-icon-plus" @click="openAddDialog">添加评估对象</el-button>
      </div>
    </div>

    <div class="table-wrapper">
      <el-table :data="filteredEmployees" stripe class="data-table" v-loading="false">
        <el-table-column label="姓名" min-width="140">
          <template slot-scope="scope">
            <div class="name-cell">
              <div class="employee-name">{{ scope.row.real_name }}</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="dept_name" label="部门" min-width="120">
          <template slot-scope="scope">{{ scope.row.dept_name || '-' }}</template>
        </el-table-column>
        <el-table-column prop="job_title" label="职位" min-width="120">
          <template slot-scope="scope">{{ scope.row.job_title || '-' }}</template>
        </el-table-column>
        <el-table-column label="风险等级" width="110" align="center">
          <template slot-scope="scope">
            <el-tag
              v-if="isEvaluated(scope.row)"
              :type="getRiskTagType(scope.row.latest_risk_level)"
              size="small"
              effect="dark"
            >
              {{ getRiskLabel(scope.row.latest_risk_level) }}
            </el-tag>
            <el-tag v-else type="info" size="small">未评估</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="最近分数" width="90" align="center">
          <template slot-scope="scope">
            <span v-if="isEvaluated(scope.row)" class="score-text">
              {{ scope.row.latest_score }} 分
            </span>
            <span v-else class="score-text muted">-</span>
          </template>
        </el-table-column>
        <el-table-column label="评估次数" width="90" align="center">
          <template slot-scope="scope">
            <span v-if="getEvalStats(scope.row).count > 0" class="score-text">
              {{ getEvalStats(scope.row).count }} 次
            </span>
            <span v-else class="score-text muted">-</span>
          </template>
        </el-table-column>
        <el-table-column label="最近评估" width="120" align="center">
          <template slot-scope="scope">
            <span v-if="getEvalStats(scope.row).latestDate" class="score-text" style="font-size:13px;">
              {{ getEvalStats(scope.row).latestDate }}
            </span>
            <span v-else class="score-text muted">-</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="240" align="center">
          <template slot-scope="scope">
            <div class="action-group">
              <el-button
                size="mini"
                :type="isEvaluated(scope.row) ? 'warning' : 'primary'"
                round
                class="action-btn"
                :disabled="$store.getters.isCurrentTaskExpired"
                @click="goEvaluate(scope.row.employee_id)"
              >
                {{ isEvaluated(scope.row) ? '重新评估' : '评估' }}
              </el-button>
              <el-button
                size="mini"
                round
                class="action-btn"
                @click="showHistory(scope.row)"
              >
                历史
              </el-button>
              <el-button
                type="text"
                size="mini"
                class="resign-link"
                @click="openResignDialog(scope.row)"
              >
                <i class="el-icon-circle-close" style="margin-right: 2px;"></i>离职
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <EmptyState v-if="filteredEmployees.length === 0" icon="el-icon-user" title="暂无下属员工" hint="请联系管理员配置您的团队成员" />

    <!-- 添加评估对象弹窗 -->
    <el-dialog v-if="isAdmin" title="添加评估对象" :visible.sync="addDialogVisible" width="480px" :append-to-body="true"
    >
      <el-form :model="addForm" label-width="80px"
      >
        <el-form-item label="姓名" required
        >
          <el-input v-model="addForm.real_name" placeholder="请输入员工姓名" />
        </el-form-item>
        <el-form-item label="邮箱"
        >
          <el-input v-model="addForm.email" placeholder="请输入员工邮箱" />
        </el-form-item>
        <el-form-item label="部门"
        >
          <el-input v-model="addForm.dept_name" placeholder="请输入部门名称" />
        </el-form-item>
        <el-form-item label="职位"
        >
          <el-input v-model="addForm.job_title" placeholder="请输入职位名称" />
        </el-form-item>
        <el-form-item label="工号"
        >
          <el-input v-model="addForm.employee_no" placeholder="请输入工号（可选）" />
        </el-form-item>
      </el-form>
      <span slot="footer"
      >
        <el-button @click="addDialogVisible = false"
        >取消</el-button
        >
        <el-button type="primary" @click="confirmAddEmployee"
        >确定</el-button
        >
      </span
      >
    </el-dialog
    >

    <!-- 评估历史弹窗 -->
    <el-dialog
      :title="historyEmployee ? historyEmployee.real_name + ' 的评估历史' : '评估历史'"
      :visible.sync="historyVisible"
      width="640px"
      :append-to-body="true"
    >
      <div v-if="historyList.length" class="history-list-dialog">
        <div
          v-for="item in historyList"
          :key="item.evaluation_id"
          class="history-dialog-item"
        >
          <div class="history-dialog-left">
            <div class="history-dialog-date">{{ item.created_at }}</div>
            <div class="history-dialog-score">
              <RiskScoreTag :score="item.total_score" :thresholds="thresholds" />
            </div>
          </div>
          <div class="history-dialog-actions">
            <el-button type="text" size="mini" @click="viewReport(item)">查看报告</el-button>
            <el-button type="text" size="mini" @click="goEvaluateFromHistory(item)">以此为基础评估</el-button>
          </div>
        </div>
      </div>
      <div v-else class="empty-state-dialog">
        <i class="el-icon-document" style="font-size: 32px; color: var(--color-border);"></i>
        <p>暂无评估记录</p>
      </div>
    </el-dialog>

    <!-- 确认离职弹窗 -->
    <el-dialog
      title="确认离职"
      :visible.sync="resignDialogVisible"
      width="480px"
      :append-to-body="true"
    >
      <div class="resign-confirm-hint">
        <i class="el-icon-warning-outline" style="color: var(--color-high); font-size: 18px;"></i>
        <span>确认将 <strong>{{ resignForm.employee_name }}</strong> 标记为已离职？操作不可撤销。</span>
      </div>
      <el-form :model="resignForm" label-width="80px" class="resign-form">
        <el-form-item label="员工姓名">
          <el-input v-model="resignForm.employee_name" disabled />
        </el-form-item>
        <el-form-item label="离职日期" required>
          <el-date-picker
            v-model="resignForm.resignation_date"
            type="date"
            placeholder="选择离职日期"
            value-format="yyyy-MM-dd"
            style="width: 100%;"
          />
        </el-form-item>
        <el-form-item label="离职原因" required>
          <el-select v-model="resignForm.resignation_reason" placeholder="请选择离职原因" style="width: 100%;">
            <el-option
              v-for="item in resignReasonOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="备注">
          <el-input
            v-model="resignForm.notes"
            type="textarea"
            :rows="3"
            placeholder="可选：补充说明..."
            maxlength="200"
            show-word-limit
          />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="resignDialogVisible = false">取消</el-button>
        <el-button type="danger" @click="confirmResign">确认离职</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import PageHeader from '../components/PageHeader.vue'
import EmptyState from '../components/EmptyState.vue'
import RiskScoreTag from '../components/RiskScoreTag.vue'
import { filterByPinyin } from '../utils/pinyinFilter'

export default {
  name: 'EmployeeSelect',
  components: { PageHeader, EmptyState, RiskScoreTag },
  data() {
    return {
      searchKey: '',
      searchDebounceTimer: null,
      historyVisible: false,
      historyEmployee: null,
      addDialogVisible: false,
      addForm: { real_name: '', email: '', dept_name: '', job_title: '', employee_no: '' },
      resignDialogVisible: false,
      resignForm: {
        employee_id: null,
        employee_name: '',
        resignation_date: '',
        resignation_reason: '',
        notes: ''
      },
      resignReasonOptions: [
        { label: '个人发展', value: '个人发展' },
        { label: '薪酬待遇', value: '薪酬待遇' },
        { label: '工作压力', value: '工作压力' },
        { label: '家庭原因', value: '家庭原因' },
        { label: '其他', value: '其他' }
      ]
    }
  },
  computed: {
    thresholds() {
      return this.$store.state.settings.settings.thresholds || { lowMax: 4, mediumMax: 10 }
    },
    employees() {
      const user = this.$store.state.user
      const email = user && user.email ? user.email : ''
      let list = []
      if (email) {
        list = this.$store.getters.subordinatesByEmail(email) || []
      } else {
        list = this.$store.state.employees.employees || []
      }
      // 只显示在职员工（employment_status === 1），且排除当前用户自己
      return list.filter(e => e.employment_status === 1 && e.email !== email)
    },
    evaluations() {
      return this.$store.state.evaluations || []
    },
    filteredEmployees() {
      if (!this.searchKey) return this.employees
      const key = this.searchKey.toLowerCase()
      const byPinyin = filterByPinyin(this.employees, this.searchKey)
      if (byPinyin.length > 0 && this.searchKey.length >= 2) return byPinyin
      return this.employees.filter(e =>
        e.real_name.includes(key)
      )
    },
    highRiskCount() {
      return this.employees.filter(e => e.latest_risk_level === 3).length
    },
    mediumRiskCount() {
      return this.employees.filter(e => e.latest_risk_level === 2).length
    },
    lowRiskCount() {
      return this.employees.filter(e => e.latest_risk_level === 1).length
    },
    unratedCount() {
      return this.employees.filter(e => !e.latest_risk_level).length
    },
    isAdmin() {
      const user = this.$store.state.user
      return user && (user.role === 'admin' || user.role === 'super_admin')
    },
    historyList() {
      if (!this.historyEmployee) return []
      const taskId = this.$store.state.tasks && this.$store.state.tasks.currentTask && this.$store.state.tasks.currentTask.task_id
      let list = this.evaluations.filter(e => e.employee_id === this.historyEmployee.employee_id)
      if (taskId) {
        list = list.filter(e => e.task_id === taskId || !e.task_id)
      }
      return list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    }
  },
  methods: {
    handleSearch() {
      // 300ms 防抖，减少输入时的实时过滤开销
      clearTimeout(this.searchDebounceTimer)
      this.searchDebounceTimer = setTimeout(() => {
        // filteredEmployees 为计算属性，自动响应 searchKey 变化
      }, 300)
    },
    openAddDialog() {
      this.addForm = { real_name: '', email: '', dept_name: '', job_title: '', employee_no: '' }
      this.addDialogVisible = true
    },
    confirmAddEmployee() {
      if (!this.addForm.real_name.trim()) {
        this.$message.error('姓名不能为空')
        return
      }
      const empId = Date.now()
      const emp = {
        employee_id: empId,
        employee_no: this.addForm.employee_no || ('Z' + empId),
        real_name: this.addForm.real_name.trim(),
        email: this.addForm.email.trim() || ('emp' + empId + '@company.com'),
        dept_name: this.addForm.dept_name.trim() || '',
        job_title: this.addForm.job_title.trim() || '',
        manager_email: this.$store.state.user ? this.$store.state.user.email : '',
        hire_date: new Date().toISOString().slice(0, 10),
        latest_score: 0,
        latest_risk_level: null,
        employment_status: 1,
        identity: 'employee',
        task_id: this.$store.getters.currentTaskId || null
      }
      this.$store.commit('ADD_EMPLOYEE', emp)
      this.$message.success('评估对象已添加')
      this.addDialogVisible = false
    },
    isEvaluated(employee) {
      // 统一判断员工是否已评估：后端预计算字段同时有效
      return employee && employee.latest_risk_level && employee.latest_score > 0
    },
    getEvalStatus(employeeId) {
      const taskId = this.$store.state.tasks && this.$store.state.tasks.currentTask && this.$store.state.tasks.currentTask.task_id
      let list = this.evaluations.filter(e => e.employee_id === employeeId)
      if (taskId) {
        list = list.filter(e => e.task_id === taskId || !e.task_id)
      }
      if (list.length === 0) {
        return { hasEval: false, latestScore: 0, riskLevel: 0 }
      }
      const latest = list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]
      const score = latest.total_score || 0
      const riskLevel = score > this.thresholds.mediumMax ? 3 : (score > this.thresholds.lowMax ? 2 : 1)
      return { hasEval: true, latestScore: score, riskLevel }
    },
    getEvalStats(employee) {
      const employeeId = employee.employee_id
      const list = this.evaluations.filter(e => e.employee_id === employeeId)
      if (list.length === 0) {
        return { count: 0, latestDate: null }
      }
      const latest = list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]
      return {
        count: list.length,
        latestDate: latest.created_at ? latest.created_at.slice(0, 10) : '-'
      }
    },
    getRiskTagType(level) {
      const map = { 1: 'success', 2: 'warning', 3: 'danger' }
      return map[level] || 'info'
    },
    getRiskLabel(level) {
      const map = { 1: '低风险', 2: '中风险', 3: '高风险' }
      return map[level] || '-'
    },
    goEvaluate(employeeId) {
      this.$router.push('/evaluation/' + employeeId)
    },
    showHistory(employee) {
      this.historyEmployee = employee
      this.historyVisible = true
    },
    openResignDialog(employee) {
      this.resignForm = {
        employee_id: employee.employee_id,
        employee_name: employee.real_name,
        resignation_date: new Date().toISOString().slice(0, 10),
        resignation_reason: '',
        notes: ''
      }
      this.resignDialogVisible = true
    },
    async confirmResign() {
      if (!this.resignForm.resignation_date) {
        this.$message.warning('请选择离职日期')
        return
      }
      if (!this.resignForm.resignation_reason) {
        this.$message.warning('请选择离职原因')
        return
      }
      try {
        await this.$api.markResigned(this.resignForm.employee_id, {
          resignation_date: this.resignForm.resignation_date,
          resignation_reason: this.resignForm.resignation_reason
        })
        // 先同步后端最新离职数据，再进入回溯页，避免首次进入看到旧状态。
        if (this.$root && this.$root.initBackendData) {
          await this.$root.initBackendData()
        }
        this.resignDialogVisible = false
        this.$router.push({
          path: '/correction',
          query: { resigned: this.resignForm.employee_name }
        })
      } catch (e) {
        this.$message.error('标记离职失败：' + ((e.response && e.response.data && e.response.data.message) || e.message || '未知错误'))
      }
    },
    viewReport(item) {
      this.$router.push('/report/' + item.employee_id + '?evaluation_id=' + item.evaluation_id)
    },
    goEvaluateFromHistory(item) {
      this.historyVisible = false
      this.$router.push('/evaluation/' + item.employee_id + '?base_on=' + item.evaluation_id)
    }
  }
}
</script>

<style scoped>
.page-container {
  max-width: 1200px;
  margin: 0 auto;
}

/* 工具栏 */
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--space-lg);
  flex-wrap: wrap;
  gap: var(--space-md);
}

/* 搜索栏 */
.search-bar {
  position: relative;
  width: 320px;
}

.search-icon {
  position: absolute;
  left: var(--space-lg);
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-text-muted);
  font-size: 16px;
}

.search-input {
  width: 100%;
  padding: 10px 36px 10px 40px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  font-size: 14px;
  background: var(--color-surface);
  color: var(--color-text);
  transition: all var(--duration-fast) var(--ease-out);
  outline: none;
}

.search-input:focus {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(242, 201, 76, 0.15);
}

.search-input::placeholder {
  color: var(--color-text-muted);
}

.search-clear {
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-text-muted);
  cursor: pointer;
  font-size: 16px;
  padding: 6px;
  transition: color var(--duration-fast) var(--ease-out);
}

.search-clear:hover {
  color: var(--color-primary);
}

/* 统计摘要 */
.stat-summary {
  display: flex;
  gap: var(--space-md);
  font-size: 13px;
}

.stat-item {
  padding: 4px 10px;
  border-radius: var(--radius-full);
  background: var(--color-bg);
  color: var(--color-text-secondary);
}

.stat-high {
  background: var(--color-high-bg);
  color: var(--color-high-text);
}

.stat-medium {
  background: var(--color-medium-bg);
  color: var(--color-medium-text);
}

.stat-low {
  background: var(--color-low-bg);
  color: var(--color-low-text);
}

.stat-unrated {
  background: var(--color-bg);
  color: var(--color-text-muted);
}

/* 操作列按钮统一布局 */
.action-group {
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
}

.action-btn {
  min-width: 72px;
  white-space: nowrap;
}

/* 表格 */
.table-wrapper {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}

.data-table >>> th {
  background: var(--color-bg) !important;
  color: var(--color-text) !important;
  font-weight: 600 !important;
}

.name-cell {
  display: flex;
  align-items: center;
}

.employee-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
}

.employee-no {
  font-size: 12px;
  color: var(--color-text-muted);
  margin-top: 2px;
}

.score-text {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
}

.score-text.muted {
  color: var(--color-text-muted);
}

/* 历史弹窗 */
.history-list-dialog {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
}

.history-dialog-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-md) var(--space-lg);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
}

.history-dialog-left {
  display: flex;
  align-items: center;
  gap: var(--space-md);
}

.history-dialog-date {
  font-size: 13px;
  color: var(--color-text-secondary);
  min-width: 140px;
}

.history-dialog-score {
  font-size: 14px;
  font-weight: 600;
}

.history-dialog-actions {
  display: flex;
  gap: var(--space-sm);
}

.empty-state-dialog {
  text-align: center;
  padding: var(--space-3xl);
  color: var(--color-text-muted);
}

.empty-state-dialog p {
  margin-top: var(--space-md);
  font-size: 14px;
}

.resign-link {
  color: var(--color-text-muted);
  padding: 4px 6px;
  margin-left: 4px;
}
.resign-link:hover {
  color: var(--color-high);
}

.resign-confirm-hint {
  display: flex;
  align-items: flex-start;
  gap: var(--space-sm);
  padding: var(--space-md);
  background: var(--color-high-bg);
  border-radius: var(--radius-md);
  margin-bottom: var(--space-lg);
  font-size: 14px;
  color: var(--color-text);
  line-height: 1.5;
}
.resign-confirm-hint strong {
  color: var(--color-high);
}
.resign-form >>> .el-form-item__label {
  color: var(--color-text-secondary);
}

/* 响应式 */
@media (max-width: 768px) {
  .toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .search-bar {
    width: 100%;
  }

  .stat-summary {
    flex-wrap: wrap;
  }
}
</style>
